import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Typography, Button, Alert } from '@one-thd/sui-atomic-components';
import { SweepstakesMarkdown } from '../../../utils/SweepstakesMarkdown';
import { SWEEPSTAKES_CONSTANTS } from '../../../../../core/Constants';
import { windowExists } from '../../../../../common/utils/AccountUtils';
import { useResponsive } from '../../../../../hooks/use-responsive';

// Utility functions
const splitInstructions = (instructions) => {
  if (!instructions) return { mainSteps: '', importantDetails: '' };

  const parts = instructions.split('**Important Details');
  const mainSteps = parts[0]?.trim() || '';
  const importantDetails = parts[1] ? `**Important Details${parts[1]}` : '';

  return { mainSteps, importantDetails };
};

// Render components
const RenderCtaAlert = ({ showAlert, ctaTitle }) => {
  if (!showAlert) return null;

  return (
    <Alert status="info" className="sui-mb-4">
      <Typography variant="body-base">
        {ctaTitle} button was clicked! This is just a demo.
      </Typography>
    </Alert>
  );
};

const RenderDescription = ({ description }) => {
  if (!description) return null;

  return <div>{description}</div>;
};

const RenderMainSteps = ({ mainSteps }) => {
  if (!mainSteps) return null;

  return (
    <div>
      <SweepstakesMarkdown markdownText={mainSteps} />
    </div>
  );
};

const RenderImportantDetails = ({ importantDetails }) => {
  if (!importantDetails) return null;

  return (
    <div className="sui-mt-6">
      <SweepstakesMarkdown markdownText={importantDetails} />
    </div>
  );
};

const RenderFooter = ({ ctaTitle, onClick }) => {
  if (!ctaTitle) return null;

  return (
    <Button
      variant="primary"
      fullWidth
      onClick={onClick}
      data-testid="sweepstakes-step-cta-button"
    >
      {ctaTitle}
    </Button>
  );
};

export const StepContent = ({ step }) => {
  const [showCtaAlert, setShowCtaAlert] = useState(false);
  const { isNativeApp, isMobile } = useResponsive();

  if (!step) return { content: null, footer: null };

  const handleCtaClick = () => {
    const { eventBusTrigger } = step;

    if (windowExists() && eventBusTrigger) {
      if (isNativeApp && eventBusTrigger.includes(SWEEPSTAKES_CONSTANTS.EVENT_BUS.TRIGGER_PREFIX)) {
        /* Note: If sweepstakes were to leave loyalty-benefits, the trigger prefix may need to be adjusted */
        console.log('Triggering event bus event:', { eventBusTrigger });
        window.LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger(eventBusTrigger,
          { ...SWEEPSTAKES_CONSTANTS.EVENT_BUS.TRIGGER_PAYLOAD[eventBusTrigger] }
        );
      } else {
        window.open(SWEEPSTAKES_CONSTANTS.URLS.APP_STORE, '_blank');
      }
    } else {
      setShowCtaAlert(true);
      setTimeout(() => setShowCtaAlert(false), 3000);
    }
  };

  const defaultInstructions = { mainSteps: '', importantDetails: '' };
  const instructionsData = step.instructions ? splitInstructions(step.instructions) : defaultInstructions;
  const { mainSteps, importantDetails } = instructionsData;

  const content = (
    <div className="sui-space-y-4" data-testid="sweepstakes-step-content">
      <RenderCtaAlert showAlert={showCtaAlert} ctaTitle={step.ctaTitle} />
      <RenderDescription description={step.description} />
      <RenderMainSteps mainSteps={mainSteps} />
      <RenderImportantDetails importantDetails={importantDetails} />
    </div>
  );

  const footer = isMobile ? <RenderFooter ctaTitle={step.ctaTitle} onClick={handleCtaClick} /> : null;

  return { content, footer };
};

StepContent.propTypes = {
  step: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    description: PropTypes.string,
    instructions: PropTypes.string,
    ctaTitle: PropTypes.string,
    iconUrl: PropTypes.string,
    eventBusTrigger: PropTypes.string,
  }),
};

StepContent.defaultProps = {
  step: null,
};